import clsx from 'clsx'
import {FC, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'

type SettingGuideProps = {
  categoryName?: number
  className?: string
  serviceId: number
}

type SettingGuideRule = {
  id: string
  image: string
  titleId: string
  descriptionId: string
  serviceIds?: number[]
}

const SETTING_GUIDE_DATA: Record<string, SettingGuideRule[]> = {
  0: [
    {
      id: 'auto-neutral-light',
      image: '/media/stock/600x400/img-32.jpg',
      titleId: 'Order.setting.GUIDE.Auto.RULE_1.TITLE',
      descriptionId: 'Order.setting.GUIDE.Auto.RULE_1.DESC',
      serviceIds: [3, 4],
    },
    {
      id: 'auto-multi-angle',
      image: '/media/stock/600x400/img-21.jpg',
      titleId: 'Order.setting.GUIDE.Auto.RULE_2.TITLE',
      descriptionId: 'Order.setting.GUIDE.Auto.RULE_2.DESC',
      serviceIds: [3, 4],
    },
    {
      id: 'auto-stable',
      image: '/media/stock/600x400/img-63.jpg',
      titleId: 'Order.setting.GUIDE.Auto.RULE_3.TITLE',
      descriptionId: 'Order.setting.GUIDE.Auto.RULE_3.DESC',
      serviceIds: [3, 4],
    },
  ],
  2: [
    {
      id: 'indoor-angle',
      image: '/media/indoor/rule_1_shoot_above.png',
      titleId: 'Order.setting.GUIDE.Indoor.RULE_1.TITLE',
      descriptionId: 'Order.setting.GUIDE.Indoor.RULE_1.DESC',

      serviceIds: [3, 4],
    },
    {
      id: 'indoor-detail',
      image: '/media/indoor/rule_2_close_up.png',
      titleId: 'Order.setting.GUIDE.Indoor.RULE_2.TITLE',
      descriptionId: 'Order.setting.GUIDE.Indoor.RULE_2.DESC',
      serviceIds: [3, 4],
    },
    {
      id: 'indoor-no-people',
      image: '/media/indoor/rule_3_no_person.png',
      titleId: 'Order.setting.GUIDE.Indoor.RULE_3.TITLE',
      descriptionId: 'Order.setting.GUIDE.Indoor.RULE_3.DESC',
      serviceIds: [3, 4],
    },
    {
      id: 'empty-room',
      image: '/media/indoor/rule_4_empty_room.png',
      titleId: 'Order.setting.GUIDE.Indoor.RULE_4.TITLE',
      descriptionId: 'Order.setting.GUIDE.Indoor.RULE_4.DESC',
      serviceIds: [3],
    },
    {
      id: 'include-items',
      image: '/media/indoor/rule_4_include_items.png',
      titleId: 'Order.setting.GUIDE.Indoor.RULE_5.TITLE',
      descriptionId: 'Order.setting.GUIDE.Indoor.RULE_5.DESC',
      serviceIds: [4],
    },
  ],
  1: [
    {
      id: 'outdoor-light',
      image: '/media/stock/600x400/img-61.jpg',
      titleId: 'Order.setting.GUIDE.Outdoor.RULE_1.TITLE',
      descriptionId: 'Order.setting.GUIDE.Outdoor.RULE_1.DESC',
    },
    {
      id: 'outdoor-horizon',
      image: '/media/stock/600x400/img-15.jpg',
      titleId: 'Order.setting.GUIDE.Outdoor.RULE_2.TITLE',
      descriptionId: 'Order.setting.GUIDE.Outdoor.RULE_2.DESC',
    },
    {
      id: 'outdoor-declutter',
      image: '/media/stock/600x400/img-54.jpg',
      titleId: 'Order.setting.GUIDE.Outdoor.RULE_3.TITLE',
      descriptionId: 'Order.setting.GUIDE.Outdoor.RULE_3.DESC',
    },
  ],
}

const getNormalizedCategory = (categoryName?: number) => {
  if (categoryName === undefined) {
    return undefined
  }
  const match = Object.keys(SETTING_GUIDE_DATA).find((key) => Number(key) === categoryName)
  return match
}

const SettingGuide: FC<SettingGuideProps> = ({categoryName, className, serviceId}) => {
  const intl = useIntl()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState(0)
  const normalizedCategory = useMemo(() => getNormalizedCategory(categoryName), [categoryName])
  const rules = useMemo(() => normalizedCategory ? SETTING_GUIDE_DATA[normalizedCategory] : [], [normalizedCategory])
  const categoryLabel =
    normalizedCategory &&
    intl.formatMessage({id: `SETTING_REFERENCE.CATEGORY.${normalizedCategory}`})
  const containerClassName = clsx('card  bg-light-primary bg-opacity-10', className ?? 'mb-6')

  useEffect(() => {
    // Expand automatically when category changes to surface guidance without extra clicks.
    setIsCollapsed(false)
  }, [categoryName])

  useEffect(() => {
    const measureHeight = () => {
      if (bodyRef.current) {
        setContentHeight(bodyRef.current.scrollHeight)
      }
    }
    measureHeight()

    if (typeof ResizeObserver !== 'undefined' && bodyRef.current) {
      const observer = new ResizeObserver(measureHeight)
      observer.observe(bodyRef.current)
      return () => observer.disconnect()
    }

    return undefined
  }, [rules, normalizedCategory])

  useEffect(() => {
    if (!isCollapsed && bodyRef.current) {
      setContentHeight(bodyRef.current.scrollHeight)
    }
  }, [isCollapsed])

  return (
    <div className={containerClassName}>
      <div className='card-body p-4 position-relative'>
        <div className='d-flex align-items-center justify-content-between flex-wrap mb-4'>
          <div className='pe-3'>
            <div className=' fw-bold fw-bolder'>
              {intl.formatMessage({id: 'Order.setting.GUIDE.TITLE'})}
            </div>
            <div className='text-muted fs-7 mt-1'>
              {normalizedCategory
                ? intl.formatMessage(
                    {id: 'Order.setting.GUIDE.DESCRIPTION'},
                    {category: categoryLabel}
                  )
                : intl.formatMessage({id: 'Order.setting.GUIDE.PROMPT'})}
            </div>
          </div>
          <div className='d-flex align-items-center gap-2 mt-3 mt-sm-0'>
            {categoryLabel && (
              <span className='badge badge-light-primary fs-8 fw-bold'>{categoryLabel}</span>
            )}
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light-primary'
              onClick={() => setIsCollapsed((prev) => !prev)}
              aria-expanded={!isCollapsed}
              aria-controls='setting-guide-body'
              title={intl.formatMessage({id: 'Order.setting.GUIDE.TITLE'})}
            >
              <i
                className={clsx(
                  'bi fs-4',
                  isCollapsed ? 'bi-chevron-double-down' : 'bi-chevron-double-up'
                )}
              ></i>
            </button>
          </div>
        </div>

        <div
          id='setting-guide-body'
          ref={bodyRef}
          style={{
            maxHeight: isCollapsed ? 0 : contentHeight,
            opacity: isCollapsed ? 0 : 1,
            transform: isCollapsed ? 'translateY(-8px)' : 'translateY(0)',
            overflow: 'hidden',
            transition: 'max-height 0.35s ease, opacity 0.35s ease, transform 0.35s ease',
          }}
        >
          {rules.length > 0 ? (
            <div className='row g-4'>
              {rules.filter((rule) => !rule.serviceIds || rule.serviceIds.includes(serviceId)).map((rule) => (
                <div className='col-6' key={rule.id}>
                  <div className='border border-dashed rounded-3 p-4 h-100 bg-white bg-opacity-75'>
                    <div className='row g-4 align-items-center'>
                      <div className='col-12 col-lg-3'>
                        <div className='rounded-3 overflow-hidden bg-light'>
                          <img
                            src={toAbsoluteUrl(rule.image)}
                            alt={intl.formatMessage({id: rule.titleId})}
                            className='img-fluid w-100'
                            style={{objectFit: 'contain', maxHeight: '80px'}}
                          />
                        </div>
                      </div>
                      <div className='col-12 col-lg-9'>
                        <div className='fw-bold text-gray-900 mb-2'>
                          {intl.formatMessage({id: rule.titleId})}
                        </div>
                        <div className='text-muted fs-7'>
                          {intl.formatMessage({id: rule.descriptionId})}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='alert alert-secondary mb-0 py-3 px-4'>
              {intl.formatMessage({id: 'Order.setting.GUIDE.EMPTY_STATE'})}
            </div>
          )}
        </div>

        <div className='d-flex justify-content-center mt-4'></div>
      </div>
    </div>
  )
}

export {SettingGuide}
